<template>
  <div class="page-container">
    <div class="sidebar">
      <Sidebar />
    </div>
    <div class="content-wrapper">
      <!-- Error Alert -->
      <div v-if="error" class="error-alert">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
        <button class="close-alert" @click="error = null">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Success Alert -->
      <div v-if="success" class="success-alert">
        <i class="fas fa-check-circle"></i>
        {{ success }}
        <button class="close-alert" @click="success = null">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Page Title -->
      <div class="page-header">
        <h1><i class="fas fa-users"></i> Persona Keywords</h1>
        <p>Manage personas and their associated keywords</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading personas...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="personas-content">
        <!-- Stats Cards -->
        <div class="quick-stats">
          <div class="stat-card">
            <div class="stat-icon total">
              <i class="fas fa-users"></i>
            </div>
            <div class="stat-content">
              <h4>Total Personas</h4>
              <div class="stat-value">{{ personas.length }}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon keywords">
              <i class="fas fa-tags"></i>
            </div>
            <div class="stat-content">
              <h4>Personas with Keywords</h4>
              <div class="stat-value">{{ personasWithKeywords }}</div>
            </div>
          </div>
        </div>

        <!-- Personas List -->
        <div class="personas-list">
          <div 
            v-for="persona in personas" 
            :key="persona.id" 
            class="persona-card"
          >
            <div class="persona-header">
              <h3>{{ persona.name }}</h3>
              <span class="persona-id">ID: {{ persona.id }}</span>
            </div>
            <div class="persona-date">Created: {{ formatDate(persona.created_at) }}</div>
            <div class="persona-description">{{ persona.description }}</div>
            
            <div class="keywords-section">
              <div class="keywords-header">
                <h4><i class="fas fa-tags"></i> Keywords ({{ persona.keywords.length }})</h4>
                <button 
                  class="add-keywords-btn" 
                  @click="openAddKeywordsModal(persona)"
                  title="Add keywords"
                >
                  <i class="fas fa-plus"></i>
                </button>
              </div>
              
              <div v-if="persona.keywords.length === 0" class="no-keywords">
                No keywords associated with this persona.
              </div>
              <div v-else class="keywords-container">
                <div 
                  v-for="(keyword, index) in persona.keywords" 
                  :key="index" 
                  class="keyword-tag"
                >
                  {{ keyword.keyword }}
                  <button 
                    class="remove-keyword-btn" 
                    @click="confirmDeleteKeyword(persona, keyword)"
                    title="Remove keyword"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Keywords Modal -->
    <div v-if="showAddKeywordsModal" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Add Keywords to {{ selectedPersona?.name }}</h2>
          <button class="modal-close" @click="showAddKeywordsModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingKeywords" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading available keywords...</p>
          </div>
          <div v-else>
            <div class="filter-container">
              <input 
                type="text" 
                v-model="keywordFilter" 
                placeholder="Filter keywords..." 
                class="filter-input"
              />
            </div>
            <div class="keyword-options">
              <div v-if="availableKeywords.length === 0" class="no-keywords">
                No available keywords to add.
              </div>
              <div 
                v-for="keyword in filteredKeywords" 
                :key="keyword.id" 
                class="keyword-option"
              >
                <label class="checkbox-container">
                  <input 
                    type="checkbox" 
                    :value="keyword.id" 
                    v-model="selectedKeywords"
                  />
                  <span class="checkbox-label">{{ keyword.keyword }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            class="cancel-btn" 
            @click="showAddKeywordsModal = false"
          >
            Cancel
          </button>
          <button 
            class="save-btn" 
            @click="addKeywordsToPersona"
            :disabled="selectedKeywords.length === 0 || submitting"
          >
            <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
            <span v-else>Add Selected Keywords</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content confirmation-modal">
        <div class="modal-header">
          <h2>Remove Keyword</h2>
          <button class="modal-close" @click="showDeleteModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>
            Are you sure you want to remove the keyword 
            <strong>"{{ keywordToDelete?.keyword }}"</strong> 
            from <strong>{{ selectedPersona?.name }}</strong>?
          </p>
        </div>
        <div class="modal-footer">
          <button 
            class="cancel-btn" 
            @click="showDeleteModal = false"
          >
            Cancel
          </button>
          <button 
            class="delete-btn" 
            @click="deleteKeywordFromPersona"
            :disabled="submitting"
          >
            <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
            <span v-else>Remove Keyword</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'PersonaKeyword',
  components: {
    Sidebar
  },
  data() {
    return {
      personas: [],
      loading: true,
      error: null,
      success: null,
      
      // Keywords management
      availableKeywords: [],
      loadingKeywords: false,
      
      // Modal state
      showAddKeywordsModal: false,
      showDeleteModal: false,
      selectedPersona: null,
      keywordToDelete: null,
      selectedKeywords: [],
      keywordFilter: '',
      submitting: false
    };
  },
  computed: {
    personasWithKeywords() {
      return this.personas.filter(persona => persona.keywords.length > 0).length;
    },
    filteredKeywords() {
      if (!this.keywordFilter) {
        return this.availableKeywords.filter(keyword => {
          // Filter out keywords that are already associated with this persona
          const alreadyAdded = this.selectedPersona?.keywords.some(k => k.id === keyword.id);
          return !alreadyAdded;
        });
      }
      
      const filter = this.keywordFilter.toLowerCase();
      return this.availableKeywords.filter(keyword => {
        const alreadyAdded = this.selectedPersona?.keywords.some(k => k.id === keyword.id);
        return !alreadyAdded && keyword.keyword.toLowerCase().includes(filter);
      });
    }
  },
  created() {
    this.fetchPersonas();
  },
  methods: {
    async fetchPersonas() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          'https://api.kewaar.com/api/personas', 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c'}`
            }
          }
        );
        
        this.personas = response.data.data;
      } catch (error) {
        this.error = 'Failed to fetch personas. Please try again.';
        console.error('Error fetching personas:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async fetchAvailableKeywords() {
      try {
        this.loadingKeywords = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        // Fetch all available keywords
        const response = await axios.get(
          'https://api.kewaar.com/api/admin/keywords?per_page=100', 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c'}`
            }
          }
        );
        
        this.availableKeywords = response.data.data.data;
      } catch (error) {
        this.error = 'Failed to fetch available keywords. Please try again.';
        console.error('Error fetching keywords:', error);
      } finally {
        this.loadingKeywords = false;
      }
    },
    
    openAddKeywordsModal(persona) {
      this.selectedPersona = persona;
      this.selectedKeywords = [];
      this.keywordFilter = '';
      this.showAddKeywordsModal = true;
      this.fetchAvailableKeywords();
    },
    
    confirmDeleteKeyword(persona, keyword) {
      this.selectedPersona = persona;
      this.keywordToDelete = keyword;
      this.showDeleteModal = true;
    },
    
    async addKeywordsToPersona() {
      if (this.selectedKeywords.length === 0 || !this.selectedPersona) return;
      
      try {
        this.submitting = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        // Call API to add keywords to the persona
        await axios.post(
          `https://api.kewaar.com/api/personas/${this.selectedPersona.id}/keywords`,
          { keywords: this.selectedKeywords },
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c'}`
            }
          }
        );
        
        // Show success message
        this.success = `Keywords successfully added to ${this.selectedPersona.name}`;
        
        // Refresh the data
        await this.fetchPersonas();
        
        // Close modal
        this.showAddKeywordsModal = false;
      } catch (error) {
        this.error = 'Failed to add keywords. Please try again.';
        console.error('Error adding keywords:', error);
      } finally {
        this.submitting = false;
      }
    },
    
    async deleteKeywordFromPersona() {
      if (!this.selectedPersona || !this.keywordToDelete) return;
      
      try {
        this.submitting = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        // Call API to delete keyword from the persona
        await axios.delete(
          `https://api.kewaar.com/api/personas/${this.selectedPersona.id}/keywords`,
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c'}`
            },
            data: { keywords: [this.keywordToDelete.id] }
          }
        );
        
        // Show success message
        this.success = `Keyword "${this.keywordToDelete.keyword}" successfully removed from ${this.selectedPersona.name}`;
        
        // Refresh the data
        await this.fetchPersonas();
        
        // Close modal
        this.showDeleteModal = false;
      } catch (error) {
        this.error = 'Failed to remove keyword. Please try again.';
        console.error('Error removing keyword:', error);
      } finally {
        this.submitting = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 250px;
  flex-shrink: 0;
}

.content-wrapper {
  flex-grow: 1;
  padding: 24px;
  background-color: #f9fafb;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-header p {
  color: #6b7280;
  font-size: 16px;
}

.error-alert {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.success-alert {
  background-color: #dcfce7;
  color: #15803d;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-alert {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
}

.error-alert .close-alert {
  color: #dc2626;
}

.success-alert .close-alert {
  color: #15803d;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.quick-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 300px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 20px;
}

.stat-icon.total {
  background-color: #dbeafe;
  color: #3b82f6;
}

.stat-icon.keywords {
  background-color: #e0f2fe;
  color: #0ea5e9;
}

.stat-content h4 {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
}

.personas-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.persona-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.persona-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.persona-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.persona-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.persona-id {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 12px;
}

.persona-date {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
}

.persona-description {
  font-size: 14px;
  color: #374151;
  margin-bottom: 16px;
  line-height: 1.5;
}

.keywords-section {
  margin-top: 16px;
}

.keywords-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.keywords-header h4 {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-keywords-btn {
  background-color: #dbeafe;
  color: #3b82f6;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.add-keywords-btn:hover {
  background-color: #bfdbfe;
}

.no-keywords {
  color: #6b7280;
  font-style: italic;
  font-size: 14px;
}

.keywords-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  background-color: #dbeafe;
  color: #1e40af;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.remove-keyword-btn {
  background: none;
  border: none;
  color: #1e40af;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  font-size: 10px;
  padding: 0;
  transition: background-color 0.2s;
}

.remove-keyword-btn:hover {
  background-color: #bfdbfe;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.confirmation-modal {
  max-width: 450px;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: #f3f4f6;
}

.modal-body {
  padding: 20px;
  flex-grow: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.filter-container {
  margin-bottom: 16px;
}

.filter-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.keyword-options {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.keyword-option {
  padding: 8px 10px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.keyword-option:hover {
  background-color: #f9fafb;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-label {
  margin-left: 8px;
  font-size: 14px;
  color: #374151;
}

.cancel-btn, .save-btn, .delete-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
}

.cancel-btn {
  background-color: #f3f4f6;
  color: #374151;
}

.cancel-btn:hover {
  background-color: #e5e7eb;
}

.save-btn {
  background-color: #3b82f6;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.delete-btn {
  background-color: #ef4444;
  color: white;
}

.delete-btn:hover:not(:disabled) {
  background-color: #dc2626;
}

.save-btn:disabled, .delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style> 